/**
 * The visit.
 *
 * A campaign link lands somebody on `/products/aura?utm_source=insta`. They
 * read the page, open the switch's specs, come back, and send the pre-order
 * from a URL with nothing in it. Until this file existed the form island read
 * `location.search` at mount and that visit arrived as organic traffic, because
 * the only place the campaign was ever written down was an address bar the
 * visitor had already navigated away from. This is where it is written down
 * instead: once, on the first page of the visit, for as long as the tab is open.
 *
 * WHERE IT LIVES
 *
 *     sessionStorage["zt.visit.v1"] =
 *       {"v":1,"started":1754640000000,
 *        "landing":"/ar/products/aura","landingAt":1754640000000,
 *        "referrer":"https://www.google.com/search",
 *        "utm":{"source":"insta","medium":"cpc"},"referral":"majid"}
 *
 *     sessionStorage["zt.visit.pages.v1"] =
 *       [{"path":"/ar/products/aura","ts":1754640000000},
 *        {"path":"/products","ts":1754640021000}]
 *
 * Two keys, deliberately. The first record is written once and then almost
 * never again; the ring is written on every page load. Keeping them apart means
 * a ring write can never be the reason a first touch is lost -- one serialise,
 * one `setItem`, one thing that can go wrong, and the campaign is not in it.
 *
 * `sessionStorage`, not `localStorage`, for the same reason as the basket and
 * the pre-order bar: a visit is the unit this answers questions about. It dies
 * with the tab, which is exactly the retention policy we would otherwise have
 * to write, sweep and defend -- no TTL, no expiry field, nothing left on the
 * device tomorrow. It is the narrowest thing that answers "how did this person
 * get here", and the narrowest thing is the one to store.
 *
 * WHAT PDPL MAKES OF IT
 *
 * Nothing in here is a thing a person typed. No name, phone, email or message;
 * no query string, from this site or any other; no id of any kind. It is the
 * path of the pages a browser asked for, the site that sent it, the campaign
 * that named it, and four facts about the device that every HTTP request
 * already carries in its headers.
 *
 * And none of it leaves the device before submit. That is the whole design and
 * it is not a detail: consent on this site is captured by the form's own
 * checkbox, at submit, and `client()` exists so that this record can ride along
 * *with* that consent, in the same request, as part of a lead the visitor chose
 * to send. Sending it beforehand -- to a beacon, unattached, "just the
 * anonymous parts" -- would be tracking somebody who has consented to nothing,
 * and a page path plus a screen size plus a timezone is not anonymous, it is a
 * fingerprint with a timestamp on it. So: `measure.js` must never gain any of
 * this. Its beacon carries an event name and three words from a closed
 * vocabulary precisely so that it may run before consent, and the moment
 * anything here reaches it, that is no longer true of either file.
 *
 * WHAT IS PURE AND WHAT IS NOT
 *
 * Everything above "The window" is a function of its arguments: no storage, no
 * location, no clock beyond a `ts` passed in. That is what `dev-js/visit.test.mjs`
 * exercises -- the first-touch rule, the caps, the stripping and the ring all
 * without a browser. The four window-facing functions are thin over them and
 * touch the window only inside a call, so this module imports cleanly under
 * Node and can be loaded before the document is.
 *
 * IT NEVER THROWS
 *
 * `attribution()` is called by the form island while it is building a payload,
 * and `recordVisit()` is the first thing the bundle does on every page. An
 * exception from either is, respectively, a pre-order that never sends and a
 * page whose islands never mount. So every read of storage, location, document
 * and screen is inside a try/catch, a stored record that cannot be read is
 * replaced rather than mourned, and a browser that refuses storage gets a
 * module that quietly does nothing -- on such a page `attribution()` falls back
 * to reading the URL, which is exactly what the form island did before this
 * file existed.
 */

/** Bumped when the record's shape changes; an older shape is discarded. */
const VERSION = 1;

/** The first-touch record. The version is in the key as well as in the record. */
export const KEY = "zt.visit.v1";

/**
 * The page ring.
 *
 * A bare array rather than a `{v, pages}` wrapper: the version is in the key,
 * and a ring is the one record whose loss costs nothing -- an unreadable one is
 * simply started again, so there is no shape to negotiate with.
 */
export const PAGES_KEY = "zt.visit.pages.v1";

/**
 * Most pages kept.
 *
 * Twenty is a long look around a nine-page site: enough to see that somebody
 * read three product pages before the form, which is the question this answers,
 * and short enough that the record stays a few hundred bytes however long the
 * tab stays open.
 */
export const MAX_PAGES = 20;

/** Most of one path. Longer than any route this site has, by a wide margin. */
export const MAX_PATH = 120;

/** Most of a referrer, reduced to origin and path. */
export const MAX_REFERRER = 200;

/** Most of one campaign value. The same cap the form island used. */
export const MAX_VALUE = 200;

/**
 * Most of `ttf`, in seconds.
 *
 * An hour on one page is not a slow reader, it is a tab left open, and the
 * difference between that and a week is not information. Capping also keeps the
 * field a small integer whatever a machine's clock does between two `Date.now`
 * calls.
 */
export const MAX_TTF = 3600;

/** The campaign keys, unprefixed. Mirrors what `leads.submit` stores. */
const UTM_KEYS = ["source", "medium", "campaign", "content", "term"];

/**
 * When this module was loaded, which on a server-rendered site is when the page
 * was. `ttf` is measured from here rather than from `navigation.timing`, and the
 * difference is not worth a second API: this file is imported by the bundle
 * that boots the page, so the two are the same moment give or take a parse.
 */
const LOADED = Date.now();

/** A stamp, or null. Rejects strings, NaN, Infinity and times before the epoch. */
function stamp(value) {
	return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : null;
}

/** One campaign value: a non-empty trimmed string, capped. */
function cleanValue(value) {
	if (typeof value !== "string") return "";
	return value.trim().slice(0, MAX_VALUE);
}

/** The five known keys, cleaned, in a fixed order; everything else dropped. */
function cleanUtm(utm) {
	const out = {};
	if (!utm || typeof utm !== "object" || Array.isArray(utm)) return out;
	for (const key of UTM_KEYS) {
		const value = cleanValue(utm[key]);
		if (value) out[key] = value;
	}
	return out;
}

/**
 * A path, without whatever else was in the URL.
 *
 * `location.pathname` carries neither a query string nor a fragment, so the
 * stripping here is for the record's sake rather than the browser's: this runs
 * over stored entries too, and a record written by an older version -- or by
 * hand -- is not something to take at its word. It matters because a query
 * string is the one part of a URL a campaign, an email client or a person can
 * put anything at all into, and `?email=someone@example.com` in a path is a
 * stored identifier nobody meant to store.
 *
 * The `/ar` prefix stays. It is which of the two trees the page was read in,
 * which is a fact about the site and not about the reader; `measure.js` strips
 * it because a language split is a report rather than a dimension, and this is
 * the opposite case -- here it is the page's own address.
 */
export function cleanPath(value) {
	if (value === null || value === undefined) return "/";
	let path = String(value);
	const cut = path.search(/[?#]/);
	if (cut !== -1) path = path.slice(0, cut);
	path = path.trim();
	if (!path) return "/";
	if (path.charAt(0) !== "/") path = "/" + path;
	return path.slice(0, MAX_PATH);
}

/**
 * The site that sent them, or nothing.
 *
 * Same-origin referrers are dropped: an internal navigation is what the page
 * ring is for, and keeping it here as well would mean the referrer of a lead
 * sent from `/contact` is usually `/contact`. What is left is the useful half --
 * a search engine, a social app, a partner's site -- reduced to origin and path.
 *
 * The query string goes, and that is the point of reducing rather than storing:
 * a Google referrer carries the search terms, a webmail referrer carries a
 * message id, and neither is ours to keep. Only `http:` and `https:` survive;
 * an `android-app://` referrer has no origin a URL parser will name (it answers
 * `"null"`), so it is dropped rather than stored as the string "null".
 */
export function reduceReferrer(referrer, origin) {
	if (typeof referrer !== "string" || !referrer.trim()) return "";
	let url = null;
	try {
		url = new URL(referrer);
	} catch (err) {
		return "";
	}
	if (url.protocol !== "http:" && url.protocol !== "https:") return "";
	if (origin && url.origin === origin) return "";
	return (url.origin + url.pathname).slice(0, MAX_REFERRER);
}

/**
 * Campaign attribution from a query string.
 *
 * Ported from the form island, whitelist and caps intact, because it is the
 * same question asked in a better place: there it read the URL of whichever
 * page the form happened to be on, here it reads the URL of every page and
 * keeps the first answer.
 *
 * `referral` before `ref` when a link carries both: `referral` is the name in
 * the endpoint's own argument list, and a link with both is a link somebody
 * built by hand.
 */
export function parseAttribution(search) {
	const params = new URLSearchParams(search || "");
	const utm = {};
	for (const key of UTM_KEYS) {
		const value = cleanValue(params.get("utm_" + key));
		if (value) utm[key] = value;
	}
	return { utm, referral: cleanValue(params.get("referral") || params.get("ref")) };
}

/**
 * First touch wins.
 *
 * The rule in one line: a stored non-empty answer is never replaced, and a
 * stored empty one is upgraded by the first non-empty answer that arrives. Both
 * halves are load-bearing. The first is the bug this module exists to fix -- the
 * campaign that brought somebody to the site is the one that brought them, not
 * whichever link they last happened to click. The second covers the visitor who
 * landed on the home page from nowhere in particular and then clicked a
 * campaign link in another tab: their first touch simply had not happened yet,
 * and an empty record is not an answer worth defending.
 *
 * `utm` moves as a set rather than key by key. A campaign's source, medium and
 * content describe one link, and filling the gaps in one campaign's record from
 * another's would report a click that never existed.
 *
 * `referral` is decided separately, because it is a different question with a
 * different answer: a partner's code and a campaign's tags travel on different
 * links, and one of them arriving first says nothing about the other.
 */
export function mergeAttribution(stored, fresh) {
	const kept = cleanUtm(stored && stored.utm);
	const keptReferral = cleanValue(stored && stored.referral);
	return {
		utm: Object.keys(kept).length ? kept : cleanUtm(fresh && fresh.utm),
		referral: keptReferral || cleanValue(fresh && fresh.referral),
	};
}

/**
 * A stored record, normalised, or `null` for anything that is not one of ours.
 *
 * Everything it hands back has been through the same cleaners as a freshly
 * written record, because a record is untrusted input the moment it has been
 * out of this process: written by an older version, by a tab that died
 * mid-write, or by somebody's console. `null` means "there is no visit
 * recorded", which is a state the caller already handles -- it is the state of
 * every first page load -- so a corrupt record needs no separate path.
 */
export function parseRecord(raw) {
	if (typeof raw !== "string" || !raw.trim()) return null;
	let record = null;
	try {
		record = JSON.parse(raw);
	} catch (err) {
		return null;
	}
	if (!record || typeof record !== "object" || Array.isArray(record)) return null;
	if (record.v !== VERSION) return null;

	const started = stamp(record.started);
	if (started === null) return null;
	const landingAt = stamp(record.landingAt);
	return {
		v: VERSION,
		started,
		landing: cleanPath(record.landing),
		landingAt: landingAt === null ? started : landingAt,
		// No origin to compare against here: a stored referrer was already
		// checked against the origin of the page that stored it, and the point
		// of re-reducing is the query string an older or hand-written record
		// could still be carrying.
		referrer: reduceReferrer(record.referrer, ""),
		utm: cleanUtm(record.utm),
		referral: cleanValue(record.referral),
	};
}

/** A stored ring, cleaned entry by entry. Anything unreadable is an empty ring. */
export function parsePages(raw) {
	if (typeof raw !== "string" || !raw.trim()) return [];
	let list = null;
	try {
		list = JSON.parse(raw);
	} catch (err) {
		return [];
	}
	return keepLast(Array.isArray(list) ? list : []);
}

/** Valid entries only, in order, no more than the cap, oldest dropped. */
function keepLast(entries) {
	const out = [];
	for (const entry of entries) {
		if (!entry || typeof entry !== "object") continue;
		if (typeof entry.path !== "string" || !entry.path) continue;
		const ts = stamp(entry.ts);
		if (ts === null) continue;
		out.push({ path: cleanPath(entry.path), ts });
	}
	return out.length > MAX_PAGES ? out.slice(out.length - MAX_PAGES) : out;
}

/**
 * One more page, giving back a new ring.
 *
 * Past the cap the oldest entry goes, not the newest: the interesting end of a
 * long visit is the end nearest the form. Ordering is the record -- this is a
 * walk through the site, not a set of pages seen -- so entries are never sorted,
 * deduplicated or merged, and a visitor who goes back to a page they have
 * already read is a visitor who went back to it.
 */
export function pushPage(pages, path, ts) {
	const when = stamp(ts);
	const entries = Array.isArray(pages) ? pages.slice() : [];
	entries.push({ path: cleanPath(path), ts: when === null ? 0 : when });
	return keepLast(entries);
}

/* ------------------------------------------------------------------ *
 * The window
 * ------------------------------------------------------------------ */

/**
 * The window to read, or null.
 *
 * Everything below reads the environment through this one object -- storage,
 * location, document, screen, navigator, Intl -- rather than off the globals
 * directly. That is what lets the whole module be driven from Node with a plain
 * object, and it is why nothing here touches a global at import time.
 */
function env(win) {
	if (win) return win;
	return typeof window !== "undefined" ? window : null;
}

/**
 * sessionStorage, or null.
 *
 * Private windows, storage disabled by policy and quota-exhausted profiles
 * throw on access rather than answering, and a probe write is the only way to
 * tell a readable store from one that will throw on the first save. The same
 * shape as `openSession()` in `basket.js` and `preorder_cta.js`.
 */
function openSession(win) {
	try {
		const store = win.sessionStorage;
		if (!store) return null;
		const probe = "__zt_probe__";
		store.setItem(probe, "1");
		store.removeItem(probe);
		return store;
	} catch (err) {
		return null;
	}
}

/** Any property of the window, without trusting the window to have one. */
function read(win, name) {
	try {
		return win[name] || null;
	} catch (err) {
		return null;
	}
}

function getItem(session, key) {
	try {
		return session.getItem(key);
	} catch (err) {
		return null;
	}
}

function setItem(session, key, value) {
	try {
		session.setItem(key, value);
	} catch (err) {
		/* full or refused: the visit still works on this page, it just will not
		   be waiting on the next one */
	}
}

/** One record per page load: the bundle boots once, and so does this. */
const recorded = new WeakMap();

/**
 * The visit, as of this page.
 *
 * Called first by the bundle, before the islands mount, so that a form island
 * mounting on the landing page of a campaign click reads a record that already
 * exists. Idempotent per page load -- a second call answers with the same
 * record and does not add a second ring entry -- because "how many times was
 * this function called" is not a question the record should be able to answer.
 *
 * Creating it stores the landing page, its stamp, the external referrer and the
 * campaign. Finding one already there leaves all of that alone and considers
 * only the first-touch upgrade, and writes only if that changed something: a
 * visit that reads nine pages should cost one write to this key, not nine.
 *
 * @param {Window} [win]
 * @returns {object|null} the record, or null when there is no storage to keep it in
 */
export function recordVisit(win) {
	const target = env(win);
	if (!target) return null;
	if (recorded.has(target)) return recorded.get(target);

	const session = openSession(target);
	if (!session) {
		recorded.set(target, null);
		return null;
	}

	const now = Date.now();
	const loc = read(target, "location");
	const fresh = parseAttribution(loc ? loc.search : "");
	const existing = parseRecord(getItem(session, KEY));

	let record;
	if (existing) {
		const merged = mergeAttribution(existing, fresh);
		record = Object.assign({}, existing, merged);
		const before = JSON.stringify({ utm: existing.utm, referral: existing.referral });
		if (JSON.stringify(merged) !== before) setItem(session, KEY, JSON.stringify(record));
	} else {
		const doc = read(target, "document");
		record = {
			v: VERSION,
			started: now,
			landing: cleanPath(loc ? loc.pathname : "/"),
			landingAt: now,
			referrer: reduceReferrer(doc ? doc.referrer : "", loc ? loc.origin : ""),
			utm: fresh.utm,
			referral: fresh.referral,
		};
		setItem(session, KEY, JSON.stringify(record));
	}

	// The landing page is the first page of the walk, so the ring is written
	// here rather than left to the caller: two calls at boot would be two ways
	// to get it wrong, and the second one would be silent.
	trackPageView(target);
	recorded.set(target, record);
	return record;
}

/**
 * This page, appended to the walk.
 *
 * Separate from `recordVisit` because they are separate facts -- one is where
 * the visit came from, the other is where it has been -- and because the ring
 * is the half that would have to be called again if the site ever navigates
 * without a page load. It is not called twice today: `recordVisit` calls it.
 *
 * @param {Window} [win]
 * @returns {Array|null} the ring as stored, or null when there is no storage
 */
export function trackPageView(win) {
	const target = env(win);
	if (!target) return null;
	const session = openSession(target);
	if (!session) return null;

	const loc = read(target, "location");
	const pages = pushPage(
		parsePages(getItem(session, PAGES_KEY)),
		cleanPath(loc ? loc.pathname : "/"),
		Date.now()
	);
	setItem(session, PAGES_KEY, JSON.stringify(pages));
	return pages;
}

/**
 * The campaign this visit belongs to, for the form island.
 *
 * The stored first touch, or -- when there is no storage to have stored it in --
 * this page's own query string, which is exactly what the form island read
 * before this module existed. A page whose visitor refuses storage therefore
 * behaves as the site always has: attribution works on the page that was landed
 * on and is lost on the next one, rather than the form breaking.
 *
 * The two are merged rather than chosen between, by the same first-touch rule,
 * so a stored record that has no campaign in it does not silence the campaign
 * link the visitor is standing on right now.
 *
 * @param {Window} [win]
 * @returns {{utm: object, referral: string}} the same shape the payload wants
 */
export function attribution(win) {
	const target = env(win);
	if (!target) return { utm: {}, referral: "" };
	const loc = read(target, "location");
	const fresh = parseAttribution(loc ? loc.search : "");
	const session = openSession(target);
	return mergeAttribution(session ? parseRecord(getItem(session, KEY)) : null, fresh);
}

/** The screen, as three numbers, or null if the browser will not say. */
function screenOf(win) {
	const out = {};
	const display = read(win, "screen");
	if (display) {
		const w = Math.round(Number(display.width));
		const h = Math.round(Number(display.height));
		if (Number.isFinite(w) && w > 0) out.w = w;
		if (Number.isFinite(h) && h > 0) out.h = h;
	}
	let dpr = NaN;
	try {
		dpr = Number(win.devicePixelRatio);
	} catch (err) {
		dpr = NaN;
	}
	// Android reports ratios like 1.7999999523162842. Two decimals is every
	// device that has ever shipped, and a shorter field.
	if (Number.isFinite(dpr) && dpr > 0) out.dpr = Math.round(dpr * 100) / 100;
	return Object.keys(out).length ? out : null;
}

/**
 * What this visit looked like, for a submit to carry.
 *
 * Built here and attached by whoever is sending the lead, at the moment consent
 * is captured -- see the PDPL note at the head of this file for why it is never
 * sent any earlier, and why `measure.js` must never be given it.
 *
 * `utm` is deliberately not in here. It is a top-level field of the submit
 * payload with exactly one writer, and a second copy inside an enrichment blob
 * is a second answer to a question that must only have one.
 *
 * Every key is omitted rather than sent empty when its source is not there, so
 * the object is a list of things that are actually known. At worst it is `{}`,
 * and at worst it is silent about it.
 *
 * @param {Window} [win]
 * @returns {object}
 */
export function client(win) {
	const out = {};
	const target = env(win);

	// Time to form: measured from this module's load on *this* page, so it is
	// how long they spent here before sending, not how long the tab has existed.
	const ttf = Math.floor((Date.now() - LOADED) / 1000);
	out.ttf = Math.min(Math.max(Number.isFinite(ttf) ? ttf : 0, 0), MAX_TTF);
	if (!target) return out;

	try {
		const session = openSession(target);
		const record = session ? parseRecord(getItem(session, KEY)) : null;
		if (record) {
			out.landing = record.landing;
			if (record.referrer) out.referrer = record.referrer;
			out.visit_started = record.started;
		}
		const pages = session ? parsePages(getItem(session, PAGES_KEY)) : [];
		if (pages.length) out.pages = pages;
	} catch (err) {
		/* a visit that cannot be read is a submit with less in it, which is a
		   submit; there is nothing here worth failing one over */
	}

	try {
		const display = screenOf(target);
		if (display) out.screen = display;

		// Read off the window rather than the global, like everything else here.
		const intl = read(target, "Intl");
		if (intl && typeof intl.DateTimeFormat === "function") {
			const tz = new intl.DateTimeFormat().resolvedOptions().timeZone;
			if (typeof tz === "string" && tz) out.tz = tz.slice(0, 60);
		}

		const nav = read(target, "navigator");
		const lang = nav && nav.language;
		// A language tag is `ar-SA`, never a paragraph; the cap is for the case
		// where it is not a language tag at all.
		if (typeof lang === "string" && lang) out.lang = lang.slice(0, 35);
	} catch (err) {
		/* a browser that will not describe itself is not an error */
	}

	return out;
}
